import type { Mutable, PickByValueExact } from 'utility-types'

import { assert, objectEntries, type PartialReadonlyRecord, type ReadonlyNonEmptyArray, type ReadonlyRecord } from '../../../utils'

import {
    ConnectwareError,
    ConnectwareErrorType,
    type Explorer,
    type ExplorerAppState,
    selectExplorerCustomTopics,
    selectExplorerSelectedTopics,
    type Topic,
    Translation,
} from '../../../domain'

import { ExplorerUsecase } from './Explorer'

type UpdatableTopics = PickByValueExact<Explorer, readonly string[]>

export class ExplorerTopicManagementUsecase extends ExplorerUsecase {
    private readonly topicSelectors: ReadonlyRecord<keyof UpdatableTopics, (s: ExplorerAppState) => readonly string[]> = {
        customTopics: selectExplorerCustomTopics,
        selectedTopics: selectExplorerSelectedTopics,
    }

    private updateTopics (topics: readonly string[], action: 'toggle' | 'add' | 'remove', types: PartialReadonlyRecord<keyof UpdatableTopics, boolean>): void {
        const state = this.getState()

        const update: Partial<Mutable<UpdatableTopics>> = {}

        for (const [type, isActionRequired] of objectEntries(types)) {
            for (const topic of topics) {
                const currentTopics = update[type] ?? this.topicSelectors[type](state)

                const wasAdded = currentTopics.includes(topic)
                const wantsToAdd = action === 'toggle' ? !wasAdded : action === 'add'

                if (wantsToAdd === wasAdded) {
                    assert(
                        !isActionRequired,
                        new ConnectwareError(ConnectwareErrorType.STATE, 'Topic were in an unexpected state', { wasAdded, wantsToAdd, type, topic })
                    )

                    continue
                }

                update[type] = wantsToAdd ? [...currentTopics, topic] : currentTopics.filter((t) => t !== topic)
            }
        }

        this.setExplorer(update)
    }

    /**
     * (Un)subscribe selection of a topics
     */
    toggleTopics (topics: ReadonlyNonEmptyArray<Topic<false>['fullPath']>): void {
        this.updateTopics(topics, 'toggle', { selectedTopics: true })
    }

    retryTopic (fullPath: Topic<false>['fullPath']): void {
        /** De-select relevant topics, forcing a subscription change */
        this.toggleTopics([fullPath])
        /** Re-select relevant topics, forcing re-subscription */
        this.toggleTopics([fullPath])
    }

    /**
     * Remove custom topic from application und unselect it if its selected
     *
     * If custom topic cannot be found, error is thrown
     */
    removeCustomTopic (topic: Topic<false>['fullPath']): void {
        this.updateTopics([topic], 'remove', { customTopics: true, selectedTopics: false })
    }

    /**
     * Adds custom topic and selects it
     *
     * If topic existed before, error will be thrown
     * If topic is invalid, error will be thrown
     */
    addCustomTopic (topic: Topic<false>['fullPath']): void {
        if (!this.topicsService.createTopic(topic).valid) {
            throw new ConnectwareError(ConnectwareErrorType.GENERAL_BUSINESS_RULE_INFRACTION, this.translationService.translate(Translation.INVALID_TOPIC), {
                topic,
            })
        }

        this.updateTopics([topic], 'add', { customTopics: true, selectedTopics: true })
    }
}
